<template>
  <div class="tw-bg-gray-50 tw-rounded-lg tw-p-3 tw-text-sm">
    <div class="tw-flex tw-items-center tw-justify-between tw-gap-2 tw-mb-1">
      <span class="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
        Polling server <span class="tw-font-normal tw-normal-case tw-text-gray-400">DLMS</span>
      </span>
      <slot name="actions" />
    </div>
    <div class="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-1">
      <span class="tw-text-gray-500">SIM-provider</span>
      <span class="tw-font-medium">{{ meterDetail.sim_provider_name || '—' }}</span>
      <span class="tw-text-gray-500">ICCID</span>
      <span
        v-if="meterDetail.icc_id"
        class="tw-font-medium tw-font-mono tw-text-xs tw-self-center tw-break-all tw-cursor-pointer hover:tw-text-blue-600"
        title="Klik om te kopiëren"
        @click="copyToClipboard(meterDetail.icc_id)"
      >{{ meterDetail.icc_id }}</span>
      <span v-else class="tw-font-medium">—</span>
      <span class="tw-text-gray-500">Signaal</span>
      <span v-if="meterDetail.rssi_last_value != null" class="tw-font-medium tw-flex tw-items-center tw-gap-1.5">
        <span class="tw-flex-1 tw-bg-gray-200 tw-rounded-full tw-h-1.5 tw-overflow-hidden" style="max-width: 48px;">
          <span
            class="tw-block tw-rounded-full tw-h-1.5"
            :class="rssiBarClass"
            :style="{ width: rssiPercent + '%' }"
          />
        </span>
        <span class="tw-font-mono tw-text-xs" :class="rssiTextClass">{{ meterDetail.rssi_last_value }} dBm</span>
        <span class="tw-text-xs tw-text-gray-400 tw-font-normal">{{ rssiLabel }}</span>
      </span>
      <span v-else class="tw-font-medium">—</span>
    </div>

    <!-- Poll RSSI -->
    <div v-for="(chunk, index) in receivedChunks" :key="index">
      <div v-if="chunk.chunk_data?.rssi_value" class="tw-text-xs tw-text-blue-600 tw-mt-1">
        <span class="tw-font-bold">Poll RSSI:</span> {{ chunk.chunk_data.rssi_value }} dBm
        <span v-if="chunk.chunk_data.rssi_message" class="tw-text-gray-500"> – {{ chunk.chunk_data.rssi_message }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { copyToClipboard } from '@/util/Clipboard'
import { RSSI_THRESHOLDS } from './constants'

// What only a meter read by our polling server (DLMS over its own SIM) has: SIM and signal. The SIM details
// (live at 1NCE, the database record) open in the SIM modal of MeterPanel.
const props = defineProps({
  meterDetail: { type: Object, required: true },
  receivedChunks: { type: Array, default: () => [] },
})

const rssi = computed(() => Number(props.meterDetail?.rssi_last_value))

const rssiTextClass = computed(() => {
  if (rssi.value >= RSSI_THRESHOLDS.good) return 'tw-text-green-600'
  if (rssi.value > RSSI_THRESHOLDS.moderate) return 'tw-text-amber-600'
  return 'tw-text-red-600'
})

const rssiBarClass = computed(() => {
  if (rssi.value >= RSSI_THRESHOLDS.good) return 'tw-bg-green-500'
  if (rssi.value > RSSI_THRESHOLDS.moderate) return 'tw-bg-amber-500'
  return 'tw-bg-red-500'
})

const rssiPercent = computed(() =>
  Math.max(0, Math.min(100, ((rssi.value + 100) / 70) * 100))
)

const rssiLabel = computed(() => {
  if (rssi.value >= RSSI_THRESHOLDS.good) return 'Uitstekend'
  if (rssi.value > RSSI_THRESHOLDS.moderate) return 'Matig'
  return 'Slecht'
})
</script>
